import { useMemo } from 'react'
import { useParams } from 'react-router'
import toInteger from 'lodash/toInteger'
import toString from 'lodash/toString'
import { CURRENCY_CODE_OPTIONS, normalizeCurrencyCode } from '@klear/constants/currency'
import { useSuppliers } from '@klear/ui/hooks/use-suppliers'
import { useOperationals } from '@klear/ui/hooks/use-operationals'
import { Button } from '@klear/ui/components/button'
import { Card } from '@klear/ui/components/card'
import { Combobox } from '@klear/ui/components/combobox'
import { Label } from '@klear/ui/components/label'
import type { WizardStepComponentProps } from '@klear/ui/wizard/flow'
import type { PayableStepCtx } from '@/app/payable/workflow/PayableWizardContext'

export default function SupplierStep({ ctx }: WizardStepComponentProps<PayableStepCtx>) {
  const { traderId: traderIdParam } = useParams<{ traderId?: string }>()
  const traderId = toInteger(traderIdParam)
  const { form, patchForm, patchFormAndGoNext, operationalLinkLocked, payable, previewResult } = ctx
  const { suppliers, isLoading } = useSuppliers(traderId)

  const options = useMemo(
    () => suppliers.map((supplier) => ({ label: supplier.name, value: toString(supplier.id) })),
    [suppliers]
  )

  const seededOrderId = form.orderId
  const { operationals: orderOperationals } = useOperationals(
    { order: seededOrderId != null && seededOrderId > 0 ? [seededOrderId] : [] },
    { enabled: form.supplierId == null && seededOrderId != null && seededOrderId > 0 }
  )
  const suppliersOnTrade = useMemo(() => {
    const ids = new Set<number>()
    for (const operational of orderOperationals) {
      if (operational.supplierId != null) ids.add(toInteger(operational.supplierId))
    }
    return [...ids].filter((id) => id > 0)
  }, [orderOperationals])

  const resolvedFromTrade = suppliersOnTrade.length === 1 ? (suppliersOnTrade[0] ?? null) : null

  const serverPayable = previewResult ?? payable
  const selectedId =
    form.supplierId ??
    (serverPayable?.supplierId != null ? toInteger(serverPayable.supplierId) : null) ??
    resolvedFromTrade
  const selectedName = serverPayable?.supplierName ?? suppliers.find((s) => s.id === selectedId)?.name ?? null

  if (operationalLinkLocked) {
    return (
      <div className="space-y-6 w-full max-w-2xl">
        <Card className="p-6 space-y-2">
          <h2 className="text-lg font-semibold text-foreground">Supplier</h2>
          <p className="text-sm text-muted-foreground">
            This payment pays <span className="font-medium text-foreground">{selectedName ?? 'this supplier'}</span>.
            Every event it covers bills them, so changing it would mean unlinking all of them — delete the payment and
            start again instead.
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 w-full max-w-2xl">
      <Card className="p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Who are you paying?</h2>
          <p className="text-sm text-muted-foreground">
            One payment pays one supplier. Next you will choose which of their costs it covers — from any of their
            trades.
          </p>
        </div>
        {suppliersOnTrade.length > 1 ? (
          <p className="text-sm text-muted-foreground">
            This trade has costs from {suppliersOnTrade.length} suppliers. A payment pays one, so pick which.
          </p>
        ) : null}
        {resolvedFromTrade != null && form.supplierId == null ? (
          <p className="text-sm text-muted-foreground">
            Filled in from the trade you came from — every supplier cost on it bills{' '}
            <span className="font-medium text-foreground">{selectedName ?? 'this supplier'}</span>. Change it if that is
            not who this payment is for.
          </p>
        ) : null}
        <div className="space-y-2">
          <Label>Currency</Label>
          <Combobox
            placeholder="Select a currency"
            options={CURRENCY_CODE_OPTIONS}
            value={form.currency}
            onChange={(value) => {
              const next = normalizeCurrencyCode(value)
              if (next == null) return
              patchForm((draft) => {
                draft.currency = next
              }, 'currency')
            }}
          />
        </div>
        <div className="space-y-2">
          <Label>Supplier</Label>
          <Combobox
            placeholder={isLoading ? 'Loading suppliers…' : 'Select a supplier'}
            options={options}
            value={selectedId != null ? toString(selectedId) : ''}
            onChange={(value) => {
              if (!value) return
              const nextSupplierId = toInteger(value)
              patchForm(
                (draft) => {
                  if (draft.supplierId !== nextSupplierId) {
                    draft.operationalEventIds = []
                  }
                  draft.supplierId = nextSupplierId
                },
                ['supplierId', 'operationalEventIds']
              )
            }}
          />
        </div>
        <div className="flex justify-end">
          <Button
            disabled={selectedId == null}
            onClick={() =>
              patchFormAndGoNext(
                (draft) => {
                  if (draft.supplierId !== selectedId) {
                    draft.operationalEventIds = []
                  }
                  draft.supplierId = selectedId
                },
                ['supplierId', 'operationalEventIds']
              )
            }
          >
            Continue
          </Button>
        </div>
      </Card>
    </div>
  )
}
